import type {
  ReportSchedule,
  ScheduleSetting,
} from '@components/SitemapReport/schedule'

// Monday..Sunday as 1..7, the numbers the API stores.
export const WEEKDAYS = [
  { long: 'Monday', short: 'Mon', value: 1 },
  { long: 'Tuesday', short: 'Tue', value: 2 },
  { long: 'Wednesday', short: 'Wed', value: 3 },
  { long: 'Thursday', short: 'Thu', value: 4 },
  { long: 'Friday', short: 'Fri', value: 5 },
  { long: 'Saturday', short: 'Sat', value: 6 },
  { long: 'Sunday', short: 'Sun', value: 7 },
]

// A day that only some months have would make a schedule skip those months, so
// the list stops where every month still has one.
export const MONTH_DAYS = Array.from({ length: 28 }, (_, index) => index + 1)

export const HOURS = Array.from({ length: 24 }, (_, index) => index)

export const SCHEDULE_OPTIONS: { label: string; value: ReportSchedule }[] = [
  { label: 'Disabled', value: 'off' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
]

// The picker runs on a 24-hour clock so the hours sort the way they are read;
// the "next crawl" line below it is prose, and stays on the 12-hour one.
export const formatHour24 = (hour: number) =>
  `${String(hour).padStart(2, '0')}:00`

const ordinal = (day: number) => {
  const tens = day % 100

  if (tens >= 11 && tens <= 13) {
    return `${day}th`
  }

  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th'

  return `${day}${suffix}`
}

export const daysLabel = (schedule: ReportSchedule, days: number[]) => {
  if (days.length !== 1) {
    return `${days.length} days`
  }

  if (schedule === 'weekly') {
    const weekday = WEEKDAYS.find((day) => day.value === days[0])

    return `Every ${weekday?.short ?? ''}`
  }

  return `Every ${ordinal(days[0])} day`
}

const isoWeekday = (date: Date) => date.getDay() || 7

// The browser runs in the zone the schedule is set in, so the preview is plain
// local date arithmetic — no offsets to chase. The API works the same answer out
// again on save; this one only has to say what the dialog is about to do.
export const nextRun = (setting: ScheduleSetting): Date | null => {
  if (setting.schedule === 'off') {
    return null
  }

  const now = new Date()
  const cursor = new Date(now)

  cursor.setHours(setting.hour, 0, 0, 0)

  for (let step = 0; step < 400; step += 1) {
    const due =
      setting.schedule === 'daily' ||
      (setting.schedule === 'weekly' &&
        setting.days.includes(isoWeekday(cursor))) ||
      (setting.schedule === 'monthly' &&
        setting.days.includes(cursor.getDate()))

    if (due && cursor.getTime() > now.getTime()) {
      return new Date(cursor)
    }

    cursor.setDate(cursor.getDate() + 1)
    // A day the clocks change is 23 or 25 hours long, and the hour has to be
    // put back where it was afterwards.
    cursor.setHours(setting.hour, 0, 0, 0)
  }

  return null
}
